/**
 * Canned notes for the bundled sample interview (sample-transcript.ts) —
 * the sample workspace mocks notes "generation" with this fixture instead
 * of calling the API, so visitors can explore the whole flow (including
 * editing and exports) with no key. Deliberately English-only, like the
 * sample transcript: it is content, not UI copy.
 *
 * Typed as MeetingMinutes and rendered through the same markdown → TipTap
 * pipeline as real generation, so the result is indistinguishable in the
 * editor and in exports.
 */
import type { MeetingMinutes } from "@/lib/openai/schemas/meeting-minutes";
import { renderNotesToMarkdown } from "@/lib/notes/render-markdown";
import {
  markdownToNotesDocument,
  type NotesDocumentJson,
} from "@/lib/notes/tiptap-document";

/** Long enough that the generating skeleton reads as a state, not a flash. */
const MOCK_GENERATION_MS = 600;

/** @internal Exported for the fixture-integrity test only. */
export const SAMPLE_MINUTES: MeetingMinutes = {
  title: "Interview: County Bus Network Redesign",
  date: "March 12, 2026",
  attendees: ["Reporter (interviewer)", "Senior planner, bus network redesign"],
  agenda_items: [
    {
      topic: "Service frequency shortfall",
      summary:
        "The redesign promised fifteen-minute frequencies on core routes; riders report waiting roughly twice that. The planner attributes the gap to an anticipated driver shortage and an unanticipated maintenance backlog that keeps about a fifth of the fleet out of service on any given morning.",
      decisions: [],
      action_items: [
        {
          owner: "Senior planner",
          task: "Send the reporter the last six months of monthly operations reports",
          due_date: null,
          priority: "high",
          evidence: [
            {
              segment_ids: ["sample-6"],
              quote: "I can send you the last six months.",
              confidence: "high",
            },
          ],
        },
      ],
    },
    {
      topic: "Use of the additional four million in maintenance funding",
      summary:
        "The board approved an extra four million for maintenance in March. It has gone mostly to parts and overtime; by the planner's own account it catches up on last year's deferrals rather than buying improvement.",
      decisions: [],
      action_items: [],
    },
    {
      topic: "Revised timeline and accountability",
      summary:
        "Fifteen-minute frequencies are expected on the two busiest corridors by October, but network-wide not before next summer, about a year behind the public timeline. The planner set the October date personally, put it in writing to the board, and says a miss will reach the board from him with a revised plan before any press release.",
      decisions: [],
      action_items: [],
    },
    {
      topic: "Service equity on the east side",
      summary:
        "The east side has absorbed the worst of the shortfall: Routes 40 and 62 lost their one-seat rides in the redesign, and their runs are the first to lose trips when a bus drops out. The former Route 40 alignment returns in September, which the planner calls an admission the redesign got it wrong.",
      decisions: ["Restore the old Route 40 alignment in September"],
      action_items: [],
    },
    {
      topic: "Ghost buses in the real-time app",
      summary:
        "Morning cancellations do not always reach the prediction feed, so the app keeps promising buses that dispatch already cancelled. A vendor fix is in testing; until it ships, the service alerts page is the only workaround.",
      decisions: [],
      action_items: [
        {
          owner: "Senior planner",
          task: "Ship the dispatch-to-prediction-feed cancellation fix with the app vendor",
          due_date: "End of August",
          priority: "high",
          evidence: [
            {
              segment_ids: ["sample-14"],
              quote: "I expect it live by the end of August.",
              confidence: "high",
            },
          ],
        },
      ],
    },
    {
      topic: "Electric bus order",
      summary:
        "The twenty-two-bus electric order due this spring has slipped to January while the chargers and rewired depot sit idle. Liquidated damages accrue monthly from the original delivery date, but the manufacturer is the only vendor certified to service the fleet.",
      decisions: [],
      action_items: [],
    },
  ],
  action_items: [
    {
      owner: "Senior planner",
      task: "Send the reporter the last six months of monthly operations reports",
      due_date: null,
      priority: "high",
      evidence: [
        {
          segment_ids: ["sample-6"],
          quote: "I can send you the last six months.",
          confidence: "high",
        },
      ],
    },
    {
      owner: "Senior planner",
      task: "Ship the dispatch-to-prediction-feed cancellation fix with the app vendor",
      due_date: "End of August",
      priority: "high",
      evidence: [
        {
          segment_ids: ["sample-14"],
          quote: "I expect it live by the end of August.",
          confidence: "high",
        },
      ],
    },
    {
      owner: "County board",
      task: "Decide on funding for thirty additional drivers and a second maintenance shift",
      due_date: null,
      priority: "high",
      evidence: [
        {
          segment_ids: ["sample-24"],
          quote: "Both are funding questions, and both are in front of the board right now.",
          confidence: "high",
        },
      ],
    },
  ],
  open_questions: [
    {
      question: "Will network-wide fifteen-minute frequencies actually arrive by next summer?",
      context:
        "The planner's 'realistically, not before next summer' is already a year behind the published timeline and depends on funding decisions still before the board.",
      evidence: [
        {
          segment_ids: ["sample-10"],
          quote: "Network-wide, realistically, not before next summer.",
          confidence: "high",
        },
      ],
    },
    {
      question: "Why was the fleet-availability figure not communicated more visibly?",
      context:
        "The one-in-five out-of-service number appears only in monthly operations reports the planner concedes are not easy to find.",
      evidence: [
        {
          segment_ids: ["sample-6"],
          quote: "I'll grant you those aren't easy to find.",
          confidence: "medium",
        },
      ],
    },
    {
      question: "Will the county enforce the liquidated damages against the bus manufacturer?",
      context:
        "Counsel calls enforcement straightforward, but the manufacturer is the only vendor certified to service the fleet, and the planner frames pursuing it as an open call.",
      evidence: [
        {
          segment_ids: ["sample-20"],
          quote: "Liquidated damages accrue monthly from the original delivery date.",
          confidence: "high",
        },
      ],
    },
  ],
  risks: [
    {
      risk: "Maintenance backlog keeps about a fifth of the fleet out of service each morning",
      severity: "high",
      mitigation: "Second maintenance shift, pending a funding decision by the board",
      evidence: [
        {
          segment_ids: ["sample-4"],
          quote: "About a fifth of the fleet is out of service on any given morning.",
          confidence: "high",
        },
      ],
    },
    {
      risk: "Current funding only clears deferred maintenance and does not buy service improvement",
      severity: "medium",
      mitigation: null,
      evidence: [
        {
          segment_ids: ["sample-8"],
          quote: "It doesn't buy improvement.",
          confidence: "high",
        },
      ],
    },
    {
      risk: "Electric bus delivery slipped to January while charging infrastructure sits idle",
      severity: "medium",
      mitigation: "Liquidated damages accrue monthly; enforcement decision still open",
      evidence: [
        {
          segment_ids: ["sample-18"],
          quote: "The manufacturer moved delivery to January.",
          confidence: "high",
        },
      ],
    },
  ],
};

/**
 * Mocked counterpart of `generateNotesFromTranscript` for the sample
 * session: same document shape, no key, no API call.
 */
export async function generateSampleNotes(): Promise<NotesDocumentJson> {
  await new Promise((resolve) => setTimeout(resolve, MOCK_GENERATION_MS));
  return markdownToNotesDocument(renderNotesToMarkdown(SAMPLE_MINUTES));
}
